/**
 * Helper pour le formatage des données de l'API Road Alert
 * Gère le formatage spécifique pour les communes, listes, et autres entités
 */

import { IGlobalResponse } from '../utils/apiResponseFormatter';

/**
 * Formate une réponse API contenant une liste de communes
 * 
 * @param apiResponse - La réponse brute de l'API
 * @returns Un objet formaté avec les données des communes
 */
export const formatCommunesResponse = (apiResponse: any): IGlobalResponse<any[]> => {
  // Si c'est déjà un IGlobalResponse formaté
  if (apiResponse && typeof apiResponse === 'object' && 'success' in apiResponse && 'data' in apiResponse) {
    // S'assurer que data est un tableau
    const data = Array.isArray(apiResponse.data) ? apiResponse.data : (apiResponse.data ? [apiResponse.data] : []);
    return {
      ...apiResponse,
      data: data
    };
  }

  // Si c'est une réponse brute de l'API
  if (apiResponse && typeof apiResponse === 'object') {
    // Chercher les données dans différents formats possibles
    let communes: any[] = [];
    
    if (Array.isArray(apiResponse)) {
      communes = apiResponse;
    } else if (apiResponse.items && Array.isArray(apiResponse.items)) {
      communes = apiResponse.items;
    } else if (apiResponse.data && Array.isArray(apiResponse.data)) {
      communes = apiResponse.data;
    } else if (apiResponse.data && typeof apiResponse.data === 'object') {
      const d = apiResponse.data as Record<string, unknown>;
      if (Array.isArray(d.mapList)) communes = d.mapList;
      else if (Array.isArray(d.items)) communes = d.items;
      else if (Array.isArray(d.content)) communes = d.content;
    } else if (apiResponse.mapList && Array.isArray(apiResponse.mapList)) {
      communes = apiResponse.mapList;
    } else if (apiResponse.content && Array.isArray(apiResponse.content)) {
      communes = apiResponse.content;
    } else if (apiResponse.item && !Array.isArray(apiResponse.item)) {
      communes = [apiResponse.item];
    }

    return {
      success: !apiResponse.hasError,
      message: apiResponse.status?.message || '',
      data: communes,
      other: {
        count: apiResponse.count ?? communes.length,
      },
      errors: undefined,
      status: parseInt(apiResponse.status?.code || '200', 10) || 200
    };
  }

  // Par défaut, retourner un tableau vide
  return {
    success: false,
    message: 'Aucune donnée disponible',
    data: [],
    other: {
      count: 0,
    },
    errors: undefined,
    status: 200
  };
};

/**
 * Formate une réponse API contenant une liste générique
 * 
 * @param apiResponse - La réponse brute de l'API
 * @returns Un objet formaté avec les données de la liste
 */
export const formatListResponse = (apiResponse: any): IGlobalResponse<any[]> => {
  // Si c'est déjà un IGlobalResponse formaté
  if (apiResponse && typeof apiResponse === 'object' && 'success' in apiResponse && 'data' in apiResponse) {
    // S'assurer que data est un tableau
    const data = Array.isArray(apiResponse.data) ? apiResponse.data : (apiResponse.data ? [apiResponse.data] : []);
    return {
      ...apiResponse,
      data: data
    };
  }

  // Si c'est une réponse brute de l'API
  if (apiResponse && typeof apiResponse === 'object') {
    // Chercher les données dans différents formats possibles
    let items: any[] = [];
    
    if (Array.isArray(apiResponse)) {
      items = apiResponse;
    } else if (apiResponse.items && Array.isArray(apiResponse.items)) {
      items = apiResponse.items;
    } else if (apiResponse.data) {
      // Si data existe, vérifier si c'est un tableau ou un objet unique
      if (Array.isArray(apiResponse.data)) {
        items = apiResponse.data;
      } else if (apiResponse.data.items && Array.isArray(apiResponse.data.items)) {
        items = apiResponse.data.items;
      } else if (apiResponse.data.mapList && Array.isArray(apiResponse.data.mapList)) {
        items = apiResponse.data.mapList;
      } else if (apiResponse.data.content && Array.isArray(apiResponse.data.content)) {
        items = apiResponse.data.content;
      } else if (apiResponse.data.item && Array.isArray(apiResponse.data.item)) {
        items = apiResponse.data.item;
      } else if (apiResponse.data.item && !Array.isArray(apiResponse.data.item)) {
        items = [apiResponse.data.item];
      } else if (typeof apiResponse.data === 'object' && apiResponse.data.id !== undefined) {
        // Si data est un objet unique avec un id, le convertir en tableau
        items = [apiResponse.data];
      }
    } else if (apiResponse.mapList && Array.isArray(apiResponse.mapList)) {
      items = apiResponse.mapList;
    } else if (apiResponse.content && Array.isArray(apiResponse.content)) {
      items = apiResponse.content;
    } else if (apiResponse.item) {
      if (Array.isArray(apiResponse.item)) {
        items = apiResponse.item;
      } else if (!Array.isArray(apiResponse.item)) {
        items = [apiResponse.item];
      }
    } else if (typeof apiResponse === 'object' && apiResponse.id !== undefined && !Array.isArray(apiResponse)) {
      // Si la réponse elle-même est un objet unique avec un id
      items = [apiResponse];
    }

    return {
      success: !apiResponse.hasError,
      message: apiResponse.status?.message || apiResponse.message || '',
      data: items,
      other: {
        count: apiResponse.count ?? apiResponse.data?.count ?? items.length,
      },
      errors: undefined,
      status: parseInt(apiResponse.status?.code || apiResponse.statusCode || '200', 10) || 200
    };
  }

  // Par défaut, retourner un tableau vide
  return {
    success: false,
    message: 'Aucune donnée disponible',
    data: [],
    other: {
      count: 0,
    },
    errors: undefined,
    status: 200
  };
};

/**
 * Formate une réponse API contenant une seule entité
 * 
 * @param apiResponse - La réponse brute de l'API
 * @returns Un objet formaté avec les données de l'entité
 */
export const formatSingleResponse = (apiResponse: any): IGlobalResponse<any> => {
  // Si c'est déjà un IGlobalResponse formaté
  if (apiResponse && typeof apiResponse === 'object' && 'success' in apiResponse && 'data' in apiResponse) {
    return apiResponse;
  }

  // Si c'est une réponse brute de l'API
  if (apiResponse && typeof apiResponse === 'object') {
    let item: any = null;
    
    if (apiResponse.item !== undefined && apiResponse.item !== null) {
      item = apiResponse.item;
    } else if (apiResponse.data !== undefined && apiResponse.data !== null && !Array.isArray(apiResponse.data)) {
      item = apiResponse.data;
    } else if (!Array.isArray(apiResponse) && apiResponse.id !== undefined) {
      item = apiResponse;
    }

    return {
      success: !apiResponse.hasError,
      message: apiResponse.status?.message || '',
      data: item,
      other: {
        count: item ? 1 : 0,
      },
      errors: undefined,
      status: parseInt(apiResponse.status?.code || '200', 10) || 200
    };
  }

  // Par défaut, retourner null
  return {
    success: false,
    message: 'Aucune donnée disponible',
    data: null,
    other: {
      count: 0,
    },
    errors: undefined,
    status: 200
  };
};
